import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Search, Tags } from 'lucide-react';

import {
  clearCategories,
  matchCategoryOptions,
  selectCategories,
  summariseCategorySelection,
  tickedIds,
  toggleCategory,
  type CategoryOption,
  type CategorySelection,
} from './categorySelection';

interface ChipProps {
  selection: CategorySelection;
  options: readonly CategoryOption[];
  open: boolean;
  onToggle: () => void;
}

/**
 * The folded category filter: one chip beside the range chip, saying what the
 * sheet below is limited to — "All categories", or "3 of 12 categories" — so a
 * filtered report can never pass for the whole menu at a glance.
 */
export function CategoryFilterChip({ selection, options, open, onToggle }: ChipProps) {
  const { t } = useTranslation();
  const summary = summariseCategorySelection(selection, options);
  const label =
    summary.kind === 'all'
      ? t('reports.categoryFilter.all')
      : summary.kind === 'none'
        ? t('reports.categoryFilter.none')
        : summary.total === null
          ? t('reports.categoryFilter.selected', { count: summary.count })
          : t('reports.categoryFilter.some', { count: summary.count, total: summary.total });
  const filtered = summary.kind !== 'all';

  return (
    <button
      type="button"
      data-category-chip
      onClick={onToggle}
      aria-expanded={open}
      className={`flex min-w-0 items-center gap-2 rounded-full border bg-input px-3.5 py-1.5 text-sm transition-colors ${
        open || filtered
          ? 'border-primary text-content'
          : 'border-subtle text-content-secondary hover:border-strong hover:text-content'
      }`}
    >
      <Tags className="h-3.5 w-3.5 shrink-0 text-content-muted" aria-hidden />
      <span className="tabular truncate">{label}</span>
      <ChevronDown
        className={`h-3.5 w-3.5 shrink-0 text-content-secondary transition-transform ${
          open ? 'rotate-180' : ''
        }`}
        aria-hidden
      />
    </button>
  );
}

interface PanelProps {
  selection: CategorySelection;
  options: readonly CategoryOption[];
  /** The menu has not arrived yet: nothing to tick. */
  loading: boolean;
  error: string | null;
  onChange: (next: CategorySelection) => void;
  onDone: () => void;
}

/**
 * The unfolded picker: a search box, Select all / Clear, and the checklist.
 *
 * Every tick applies at once — a filter that waited for an Apply button would
 * be one more control that looks dead until pressed. The page debounces the
 * fetch, so ticking five boxes asks the server once.
 *
 * The list scrolls inside a fixed height rather than growing the page: on a
 * phone a long menu otherwise pushes the report itself off the screen.
 */
export function CategoryFilterPanel({
  selection,
  options,
  loading,
  error,
  onChange,
  onDone,
}: PanelProps) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');

  const shown = matchCategoryOptions(options, search);
  const shownIds = shown.map((option) => option.id);
  const ticked = tickedIds(selection, options);

  return (
    <div
      data-category-panel
      className="rounded-card border border-subtle bg-raised p-3"
      role="group"
      aria-label={t('reports.categoryFilter.label')}
    >
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative min-w-40 flex-1">
          <Search
            className="pointer-events-none absolute top-2.5 h-4 w-4 text-content-muted"
            style={{ insetInlineStart: '0.625rem' }}
            aria-hidden
          />
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t('reports.categoryFilter.search')}
            aria-label={t('reports.categoryFilter.search')}
            className="min-h-9 w-full rounded-control border border-subtle bg-input ps-8 pe-3 text-sm text-content outline-none focus:border-primary"
          />
        </div>
        <button
          type="button"
          data-category-select-all
          disabled={loading || shown.length === 0}
          onClick={() => onChange(selectCategories(selection, shownIds, options))}
          className="rounded-full border border-subtle px-3 py-1.5 text-sm text-content-secondary hover:bg-hover hover:text-content disabled:cursor-not-allowed disabled:opacity-40"
        >
          {t('reports.categoryFilter.selectAll')}
        </button>
        <button
          type="button"
          data-category-clear
          disabled={loading || shown.length === 0}
          onClick={() => onChange(clearCategories(selection, shownIds, options))}
          className="rounded-full border border-subtle px-3 py-1.5 text-sm text-content-secondary hover:bg-hover hover:text-content disabled:cursor-not-allowed disabled:opacity-40"
        >
          {t('reports.categoryFilter.clear')}
        </button>
        <button
          type="button"
          onClick={onDone}
          className="rounded-full bg-primary px-4 py-1.5 text-sm font-medium text-primary-fg hover:bg-primary-hover"
        >
          {t('reports.categoryFilter.done')}
        </button>
      </div>

      {error ? (
        <p role="alert" className="mt-2 text-sm text-danger">
          {error}
        </p>
      ) : loading ? (
        <p className="mt-2 text-sm text-content-muted">{t('common.loading')}</p>
      ) : shown.length === 0 ? (
        <p className="mt-2 text-sm text-content-muted">{t('reports.categoryFilter.noMatches')}</p>
      ) : (
        // A phone on its side has ~320px under the top bar: 288px of list left
        // the report under it 0px, so a short screen caps it at 40%.
        <ul className="mt-2 grid max-h-72 gap-0.5 overflow-y-auto overscroll-contain short:max-h-[40dvh] sm:grid-cols-2 lg:grid-cols-3">
          {shown.map((option) => (
            <li key={option.id}>
              <label className="flex min-h-9 cursor-pointer items-center gap-2.5 rounded-control px-2 text-sm text-content hover:bg-hover">
                <input
                  type="checkbox"
                  data-category-option={option.id}
                  checked={ticked.has(option.id)}
                  onChange={() => onChange(toggleCategory(selection, option.id, options))}
                  className="h-4 w-4 shrink-0 rounded-sm accent-[var(--brand-primary)]"
                />
                <span className="min-w-0 truncate">{option.name}</span>
              </label>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
